import React, { useMemo, useEffect } from 'react';
import { useSelector, getLocale, history, useIntl } from 'umi';
import ConsoleStoreRankTopFilter from '@/pages/intelligence/pages/StoreRanks/ConsoleRegion/components/ConsoleStoreRankTopFilter';
import StoreRankTopCore from '@/pages/intelligence/pages/StoreRanks/components/StoreRankTopCore';
import ExportTable from '@intelligence/components/ExportTable';
import { LANGUAGE_CN } from '@intelligence/_config/config';
import SvgIcon from '@home/components/SvgIcon';
import useStoreTopRegionOptionsMap from '@intelligence/v3/_hooks/useStoreTopRegionOptionsMap';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useStoreRankTopCore from '@/pages/intelligence/pages/StoreRanks/components/StoreRankTopCore/_hooks/useStoreRankTopCore';
import { getValidGranularity } from '@/pages/intelligence/utils/commonUtils';
import { handleRangeDate2SingleDateRangeTimeFilterExtend } from '@intelligence/utils/dateUtils';
import { getAllCountries } from '@/pages/intelligence/utils/regionSelectUtils';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import {
  // 日期筛选器
  RANGE_TIME as RANGE_TIME_SELECT_FILTER,
  // 组合国家筛选器
  COMPOSED_COUNTRY_SELECT as COMPOSED_COUNTRY_SELECT_FILTER,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { CONSOLE_ENTITY_TYPE, XBOX_PLATFORM  } from '@intelligence/v3/constants/topcharts';
import { getSortedTopsKeys } from '@/pages/intelligence/_config/storeRank/utils';

import '@/pages/intelligence/assets/scss/StoreRanks/PCGlobal/index.scss';
import './index.scss';

import type { IBaseProps, IDateType, IDateTypely } from '@/pages/intelligence/types/common';
import type { IStoreRankTopFilter } from '@/pages/intelligence/types/storeRank';

const MORE_PAGE_PATH = '/intelligence/storeRanks/ConsoleRegion/more/xbox';

const GAME_ID_KEY = 'app_id';
const BUSINESS_TYPE = 'console-xbox-store-rank-top';
const DOWNLOAD_KEY = 'consoleStoreRankTop';
const DOWNLOAD_EXCEL_LOG_CFG_KEY = 'consoleXboxStoreRankTopDownloadExcel';

// 榜单支持的时间粒度
const GRANULARITIES: IDateTypely[] = ['daily'];

// 榜单支持的时间粒度（RangeTime组件使用的）
const DATE_TYPES: IDateType[] = ['date'];

const PLATFORMS: IPlatform[] = [XBOX_PLATFORM];

// 默认筛选器数据，用于reset时、兜底
const DEFAULT_FILTER: IStoreRankTopFilter = {
  store: XBOX_PLATFORM,
  region_type: 'market',
  regions: ['us'],
  genre: [],
  sub_genre: [],
  genre_source: 'iegg',
  iegg_genre_req: [],
  granularity: 'daily',
  start_date: '',
  end_date: '',
};

type IConsoleStoreRankTopXboxProps = IBaseProps;

const ConsoleStoreRankTopXbox: React.FC<IConsoleStoreRankTopXboxProps> = (props) => {
  const {} = props;
  const { formatMessage } = useIntl();

  const { isLoading: isRegionsOptionsLoading, regionsOptions } = useStoreTopRegionOptionsMap(XBOX_PLATFORM, {
    entityType: CONSOLE_ENTITY_TYPE,
    platforms: PLATFORMS,
  });

  const { filterExtend, setFilterExtend } = useFilterExtend({});
  const filterState = useSelector((rootState: any) => rootState?.[FILTER_EXTEND_NAMESPACE]);
  // 是否操作过筛选器，判断是否继承的条件
  const isOperate = filterState?.isOperate || false;

  // 初始化筛选器数据，合并继承值、默认值
  const initialFilter = useMemo<IStoreRankTopFilter>(() => {
    if (!isOperate) return DEFAULT_FILTER;

    const filterExtendTmp = handleRangeDate2SingleDateRangeTimeFilterExtend(filterExtend);
    const rangeTimeFilterExtend = filterExtendTmp?.[RANGE_TIME_SELECT_FILTER];
    const composedCountryFilterExtend = filterExtendTmp?.[COMPOSED_COUNTRY_SELECT_FILTER]?.value;

    const granularityTmp = rangeTimeFilterExtend?.granularity || DEFAULT_FILTER?.granularity;
    const initialGranularity = getValidGranularity(granularityTmp, GRANULARITIES);
    const initialDate =
      rangeTimeFilterExtend?.start_date ||
      rangeTimeFilterExtend?.end_date ||
      DEFAULT_FILTER.start_date;

    const initialRegions =
      composedCountryFilterExtend?.countries?.length === 1
        ? composedCountryFilterExtend?.countries
        : DEFAULT_FILTER.regions;

    return {
      ...DEFAULT_FILTER,
      regions: initialRegions,
      granularity: initialGranularity,
      start_date: initialDate,
      end_date: initialDate,
    };
  }, [isOperate]);

  const {
    // 榜单数据加载状态
    isLoading,
    // 榜单数据
    tops,
    // 筛选器数据
    filter,
    // 页面初始化
    onPageInit,
    // 筛选器confirm的回调
    onFilterConfirm,
    // 筛选器reset的回调
    onFilterReset,
    // 榜单卡片点击游戏的回调
    onCardGameClick,
    getDetailGamePath,
    // 榜单卡片点击标题的回调
    onGoToStoreRankMore,
  } = useStoreRankTopCore({
    entityType: CONSOLE_ENTITY_TYPE,
    // 游戏id的取值字段
    gameIdKey: GAME_ID_KEY,
    // 榜单卡片点击标题 跳转榜单下钻页的 路由path
    morePagePath: MORE_PAGE_PATH,
    // 页面打点配置的key
    pageLogCfgKey: 'consoleStoreRankTopXbox',
    isOperate,
    // 初始化筛选器数据
    initialFilter,
    // 查询榜单数据 初始化的分页大小
    initialPageSize: 10,
  });

  useEffect(() => {
    if (isRegionsOptionsLoading) return;
    const allCountries = getAllCountries(regionsOptions);
    const initialRegions = initialFilter?.regions;
    const realRegions = allCountries?.includes(initialRegions?.[0])
      ? initialRegions
      : DEFAULT_FILTER.regions;
    const initialFilterTmp = {
      ...initialFilter,
      regions: realRegions,
    };
    if (isOperate) {
      // 如果有切换到默认值了，也把继承值跟着修改
      const newfilterExtend = {
        ...filterExtend,
        [COMPOSED_COUNTRY_SELECT_FILTER]: { value: { type: 'single', countries: realRegions } },
      };
      setFilterExtend(newfilterExtend);
    }
    onPageInit(initialFilterTmp);
  }, [isRegionsOptionsLoading, regionsOptions]);

  const locale = getLocale();

  const downloadParams = useMemo(() => {
    const store = DEFAULT_FILTER.store;
    const granularity = filter?.granularity || DEFAULT_FILTER.granularity;
    return {
      entity_type: CONSOLE_ENTITY_TYPE,
      store,
      region_type: filter?.region_type || DEFAULT_FILTER.region_type,
      regions: filter?.regions || DEFAULT_FILTER.regions,
      genre: filter?.genre || [],
      sub_genre: filter?.sub_genre || [],
      genre_source: filter?.genre_source || DEFAULT_FILTER.genre_source,
      iegg_genre_req: filter?.iegg_genre_req || [],
      granularity,
      start_date: filter?.start_date || '',
      end_date: filter?.end_date || '',
      rankingSources:
        getSortedTopsKeys(CONSOLE_ENTITY_TYPE, store, granularity) || [],
    };
  }, [filter]);

  const downloadFileName = useMemo(() => {
    const startDate = filter?.start_date || '';
    const endDate = filter?.end_date || '';
    return `Store Ranks_Console_Xbox_Top_${startDate}_${endDate}`;
  }, [filter?.start_date, filter?.end_date]);

  const handleClickMultiCountry = () => {
    const source = tops?.[0]?.name;
    if (!source) return;
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append('source', encodeURIComponent(source));
    urlSearchParams.append('isCheckAllCountries', '1');
    history.push(`${MORE_PAGE_PATH}?${urlSearchParams.toString()}`);
  };

  const multiCountryText = (
    <span className="dbi-storerank-single-country-select-footer">
      {formatMessage({ id: 'dbi_store_rank_single_country_select_footer_btn_text' })}
      <SvgIcon type="aigc-jump" />
    </span>
  );

  return (
    <StoreRankTopCore
      className="dbi-xbox dbi-console-store-rank-top dbi-console-store-rank-top-xbox"
      entityType={CONSOLE_ENTITY_TYPE}
      showDownloadTips
      inlineDownload
      gameIdKey={GAME_ID_KEY}
      isLoading={isLoading}
      loadingContentStyle={{ height: 'calc(100vh - 472px)' }}
      tops={tops}
      filter={
        <ConsoleStoreRankTopFilter
          isOperate={isOperate}
          isMonthlyRank={false}
          isRegionDisabled={false}
          regionOptions={regionsOptions}
          defaultRegions={DEFAULT_FILTER.regions}
          typeList={DATE_TYPES}
          granularity={filter?.granularity}
          startDate={filter?.start_date}
          endDate={filter?.end_date}
          multiCountryText={multiCountryText}
          onClickMultiCountry={handleClickMultiCountry}
          onConfirm={onFilterConfirm}
          onReset={onFilterReset}
        />
      }
      exportTable={
        <ExportTable
          entityType={CONSOLE_ENTITY_TYPE}
          // @ts-ignore
          businessType={BUSINESS_TYPE}
          downloadLogConfigKey={DOWNLOAD_EXCEL_LOG_CFG_KEY}
          isDisableDownload={isLoading}
          total={tops?.length ?? 0}
          tableParams={downloadParams}
          newStyleFileName={downloadFileName}
          isShowMaxDownloadRowsTips={false}
          downloadModalSize={locale === LANGUAGE_CN ? 580 : 686}
          useDownloadAmountType={true}
          useWebDownload={true}
          downloadKey={DOWNLOAD_KEY}
          regionOptions={regionsOptions}
        />
      }
      onCardTitleClick={onGoToStoreRankMore}
      onCardGameClick={onCardGameClick}
      getDetailGamePath={getDetailGamePath}
    />
  );
};

export default ConsoleStoreRankTopXbox;
